import { Card, Flex, Segmented, Skeleton } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Chart, ArcElement, CategoryScale, LinearScale, PointElement, LineElement, Filler } from 'chart.js';
import { Line } from 'react-chartjs-2';

// components
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import SupportBtn from './customerSupportBtn';
import NumberElement from 'components/number/number';
import PriceElement from '../../../../components/price/price';
import Icon from 'utils/icon';

// services
import NotificationService from 'core/services/notification';

// styles
import styles from '../pages/dashboard.module.scss';

// models
import { ShareGraph } from '../models/AccountStatus';
import { onGetInvestmentGraph } from '../services/dashboardService';
import { dateFormat } from 'utils/dateFormat';

Chart.register(ArcElement, CategoryScale, LinearScale, PointElement, LineElement, Filler);

interface SharePriceChartComponentProps {}

const SharePriceChartComponent: FunctionComponent<SharePriceChartComponentProps> = () => {
	const { t } = useTranslation();

	const [chartDataLoading, setChartDataLoading] = useState<boolean>(true);
	const [chartData, setChartData] = useState<ShareGraph>();
	const [duration, setDuration] = useState<'day' | 'week' | 'month' | '6months'>('6months');
	const [maximum, setMaximum] = useState<number>(0);

	useEffect(() => {
		getSharePriceChanges();
	}, [duration]);

	const getSharePriceChanges = async () => {
		try {
			setChartDataLoading(true);
			const response = await onGetInvestmentGraph(duration);
			setChartData(response);

			let tempMax = 0;
			response.graphData.forEach((item) => {
				if (+item.value > tempMax) {
					tempMax = +item.value;
				}
			});
			setMaximum(tempMax);

			setChartDataLoading(false);
		} catch (error) {
			setChartDataLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	return (
		<Card className="h-100">
			<Flex className="mb-4" justify="space-between" align="center">
				<h5 className="mb-0 fw-medium">{t('DASHBOARD.LAST_PRICE')}</h5>
				<Segmented
					size="middle"
					onChange={setDuration}
					value={duration}
					options={[
						{ label: t('DASHBOARD.DAY'), value: 'day' },
						{ label: t('DASHBOARD.WEEK'), value: 'week' },
						{ label: t('DASHBOARD.MONTH'), value: 'month' },
						{ label: t('DASHBOARD.SIX_MONTHS'), value: '6months' },
					]}
				/>
			</Flex>

			{chartDataLoading ? (
				<div>
					<Skeleton.Button block active style={{ width: 80, height: 23 }} />
					<Skeleton.Button block active style={{ width: 220, height: 20, marginTop: 10 }} />
				</div>
			) : (
				<div>
					<PriceElement value={+chartData?.currentValue! || 0} decimalScale={2} fontSize={20} riyalSize={16} containerStyle={{ fontWeight: 500 }} />

					<Flex align="center" gap={20}>
						<PriceElement value={+chartData?.previousValue!} decimalScale={2} color="#888" riyalSize={12} />

						<PriceElement
							value={+chartData?.valueDifference!}
							decimalScale={2}
							color={+chartData?.valueDifference! > 0 ? 'green' : +chartData?.valueDifference! < 0 ? 'red' : '#888'}
							riyalSize={12}
						/>

						<div>
							<span
								className={`status d-flex align-items-center gap-1 ${
									chartData?.difference_percentage! > 0 ? 'green' : chartData?.difference_percentage! < 0 ? 'red' : ''
								}`}
								style={{ padding: '3px 10px', borderRadius: 10 }}>
								{chartData?.difference_percentage! !== 0 && (
									<Icon name={chartData?.difference_percentage! > 0 ? 'arrowUp' : 'arrowDown'} height={15} width={9} />
								)}
								<NumberElement value={chartData?.difference_percentage!} decimalScale={2} />%
							</span>
						</div>
					</Flex>
				</div>
			)}

			{chartDataLoading ? (
				<div className="mt-5">
					<LoadingComponent />
				</div>
			) : (
				<div className={styles.chart}>
					<Flex justify="flex-end">
						<PriceElement value={maximum} decimalScale={2} color="#888" riyalSize={12} />
					</Flex>
					<div>
						<Line
							data={{
								labels: chartData?.graphData.map((item) => dateFormat(item.datetime)),
								datasets: [
									{
										data: chartData?.graphData.map((item) => item.value),
										fill: true,
										borderColor: '#2884ef',
										borderWidth: 2,
										tension: 0.05,
										backgroundColor: '#e9f2fd',
									},
								],
							}}
							options={{
								responsive: true,
								maintainAspectRatio: false,
								scales: {
									x: {
										grid: {
											display: false,
										},
										ticks: {
											display: false,
										},
									},

									y: {
										grid: {
											display: false,
										},
										ticks: {
											display: false,
										},
									},
								},
							}}
						/>
					</div>
					<Flex justify="flex-end">
						<PriceElement value={+chartData?.min?.value.replaceAll(',', '')!} decimalScale={2} color="#888" riyalSize={12} />
					</Flex>
				</div>
			)}
		</Card>
	);
};

export default SharePriceChartComponent;
